import { Stack } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePerKeyDebounce } from "@/frontend/utils";
import type { AttendanceStatus, EventRecord } from "@/types/event-attendance";
import HockeyPlannerRoot from "../../components/HockeyPlannerRoot";
import {
	createFrontendAuthClient,
	createFrontendTrpcClient,
	groupByMonth,
} from "../../utils";
import { useAttendanceDraft, useAttendanceInfo, useAuthStatus } from "./hooks";
import ProfileCompletionCard from "./ProfileCompletionCard";
import { SignupCard } from "./SignupCard";
import {
	EventsContainer,
	LoadingRow,
	MonthSection,
	PastEventsSection,
	WelcomeText,
} from "./ui";

// Small tRPC client with cookies enabled
const trpcClient = createFrontendTrpcClient();

// Better Auth client
const authClient = createFrontendAuthClient();

export default function EventsListView() {
	const navigate = useNavigate();
	const qc = useQueryClient();
	const { data: meData, error: meError } = useAuthStatus(() =>
		trpcClient.user.getOwn.query(),
	);
	const loggedIn = !!meData && !meError;
	const myId = meData?.user?.id;
	const isManager = meData?.user?.is_manager ?? false;
	const needsProfileCompletion =
		loggedIn &&
		meData?.user &&
		(!meData.user.firstname?.trim() ||
			!meData.user.lastname?.trim() ||
			!meData.user.mobile?.trim());

	const { data: eventsData, isLoading: eventsLoading } = useQuery({
		queryKey: ["events", { includePast: true }],
		queryFn: () => trpcClient.event.get.query({ includePast: true }),
	});
	const allEvents: EventRecord[] = eventsData?.events ?? [];

	// Separate past and future events (use Europe/Berlin day boundaries)
	const today = DateTime.now().setZone("Europe/Berlin").startOf("day");
	const events = allEvents.filter(
		(ev) =>
			DateTime.fromISO(ev.date, { zone: "Europe/Berlin" }).startOf("day") >=
			today,
	);
	const pastEvents = allEvents
		.filter(
			(ev) =>
				DateTime.fromISO(ev.date, { zone: "Europe/Berlin" }).startOf("day") <
				today,
		)
		.sort(
			(a, b) =>
				DateTime.fromISO(b.date, { zone: "Europe/Berlin" }).toMillis() -
				DateTime.fromISO(a.date, { zone: "Europe/Berlin" }).toMillis(),
		);

	// Local draft (for anonymous users)
	const { localMap, setLocalMap, clearDraft } = useAttendanceDraft();

	const [pastExpanded, setPastExpanded] = useState(false);
	const [pendingById, setPendingById] = useState<Record<number, boolean>>({});
	const [globalSaving, setGlobalSaving] = useState(false);

	const [errorMsg, setErrorMsg] = useState<string | null>(null);
	const [successMsg, setSuccessMsg] = useState<string | null>(null);
	const { schedule } = usePerKeyDebounce(500);
	const attendanceInfo = useAttendanceInfo({
		allEvents,
		loggedIn,
		myId,
		localMap,
	});

	const setAttendanceMutation = useMutation({
		mutationFn: async (vars: { ids: number[]; status: AttendanceStatus }) =>
			trpcClient.event.setAttendance.mutate({
				ids: vars.ids,
				status: vars.status,
			}),
		onError: (err) =>
			setErrorMsg((err as Error)?.message ?? "Fehler beim Speichern"),
		onSettled: async () => {
			await Promise.all([
				qc.invalidateQueries({ queryKey: ["events"] }),
				qc.invalidateQueries({ queryKey: ["me"] }),
			]);
		},
	});

	const handleToggle = useCallback(
		(eventId: number, status: AttendanceStatus | null) => {
			if (loggedIn) {
				setPendingById((s) => ({ ...s, [eventId]: true }));
				schedule(String(eventId), async () => {
					try {
						if (status !== null)
							await setAttendanceMutation.mutateAsync({
								ids: [eventId],
								status,
							});
					} catch (e) {
						setErrorMsg((e as Error)?.message ?? "Fehler beim Speichern");
					} finally {
						setPendingById((s) => ({ ...s, [eventId]: false }));
					}
				});
			} else {
				setLocalMap((m) => {
					const copy = { ...m };
					if (status === null) delete copy[eventId];
					else copy[eventId] = status;
					return copy;
				});
			}
		},
		[loggedIn, schedule, setAttendanceMutation, setLocalMap],
	);

	const syncLocalToBackend = useCallback(async () => {
		const yesIds = Object.entries(localMap)
			.filter(([, v]) => v === "yes")
			.map(([k]) => Number(k));
		const noIds = Object.entries(localMap)
			.filter(([, v]) => v === "no")
			.map(([k]) => Number(k));
		setGlobalSaving(true);
		try {
			if (yesIds.length)
				await trpcClient.event.setAttendance.mutate({
					status: "yes",
					ids: yesIds,
				});
			if (noIds.length)
				await trpcClient.event.setAttendance.mutate({
					status: "no",
					ids: noIds,
				});
			clearDraft();
			await Promise.all([
				qc.invalidateQueries({ queryKey: ["me"] }),
				qc.invalidateQueries({ queryKey: ["events"] }),
			]);
		} catch (e) {
			setErrorMsg((e as Error)?.message ?? "Fehler beim Speichern");
		} finally {
			setGlobalSaving(false);
		}
	}, [localMap, qc, clearDraft]);

	const months = useMemo(() => groupByMonth(events), [events]);



	return (
		<HockeyPlannerRoot title="Termine">
			<EventsContainer
				saving={setAttendanceMutation.isPending || globalSaving}
				errorMsg={errorMsg}
				successMsg={successMsg}
				onCloseError={() => setErrorMsg(null)}
				onCloseSuccess={() => setSuccessMsg(null)}
			>
				<Stack spacing={3}>
					{eventsLoading && <LoadingRow />}
					{!loggedIn && <WelcomeText />}
					{needsProfileCompletion && meData.user && (
						<ProfileCompletionCard
							user={meData.user}
							setErrorMsg={setErrorMsg}
							setSuccessMsg={setSuccessMsg}
						/>
					)}
					{months.map(([monthKey, evs]) => (
						<MonthSection
							key={monthKey}
							monthKey={monthKey}
							events={evs}
							attendanceInfo={attendanceInfo}
							pendingById={pendingById}
							loggedIn={loggedIn}
							isManager={isManager}
							onToggle={handleToggle}
						/>
					))}
					<PastEventsSection
						pastEvents={pastEvents}
						pastExpanded={pastExpanded}
						setPastExpanded={setPastExpanded}
						attendanceInfo={attendanceInfo}
						loggedIn={loggedIn}
						isManager={isManager}
					/>
					{!loggedIn && (
						<SignupCard
							events={events}
							localAttendance={localMap}
							authClient={authClient}
							onSyncLocalToBackend={syncLocalToBackend}
							onErrorMsg={(msg) => setErrorMsg(msg)}
							onNavigateLogin={() => navigate("/")}
						/>
					)}
				</Stack>
			</EventsContainer>
		</HockeyPlannerRoot>
	);
}
